<script lang="ts">
  import type { ComponentType } from 'svelte';

  import { ArrowLeft } from 'lucide-svelte';

  import ScreenHeader from '$lib/components/ScreenHeader.svelte';

  /**
   * The shape of every screen that is not the data view.
   *
   * # The rules, in one place
   *
   * A non-data view is a column: a title, then content, then — if it has
   * one — a footer. The content is centred at one of two widths — see `wide`
   * below — and the header and footer are constrained to the same width so all
   * three share a left edge. The bands themselves span the window, because a
   * panel floating in the middle of a wide screen is the dialog we just spent
   * the afternoon removing.
   *
   * Writing the rules down as a component rather than as a convention is the
   * difference between them holding and them drifting: the guide, the order
   * form and the industry picker had each arrived at a different width, a
   * different padding and a different heading size by doing it themselves.
   *
   * # The header scrolls with the content, and that is what keeps it aligned
   *
   * It sat outside the scroll container at first, which looks identical until
   * the content is long enough to scroll: the scrollbar narrows the scrolling
   * box, `mx-auto` then centres the body inside something narrower than the
   * header, and the two stop sharing a left edge. Two elements can only be
   * relied upon to align if they are laid out in the same box.
   *
   * `scrollbar-gutter: stable` keeps that width from changing as content grows
   * past the fold.
   *
   * # Not for results
   *
   * The data view is the thing itself rather than a step being walked through,
   * it wants the full width for rows, and the top bar already names where you
   * are.
   */
  export let icon: ComponentType | null = null;
  export let title: string;
  export let subtitle: string = '';
  export let step: number = 0;
  export let steps: number = 0;

  /**
   * Room for a screen that reads across rather than down.
   *
   * The default width is set by a step in the guide: one question, a control
   * under it, and a line of prose that stays readable. A company profile is a
   * different shape — tables of people with eight columns, a map beside an
   * address — and at that width its columns fight for space while the window
   * has room to spare on both sides.
   *
   * A second width rather than a free one, so this stays two answers a screen
   * chooses between instead of the per-screen drift the component replaced.
   */
  export let wide: boolean = false;

  /**
   * The way out of this screen, shown on its own row above the title.
   *
   * Optional, and separate from anything in the footer. A guide step already
   * has "back" down there, and that one means *the previous question*; this
   * one means *leave*. Putting both in the same row would be two controls with
   * the same word and different consequences.
   *
   * Above the title rather than beside it, which is where the company profile
   * put its own and for the same reason: leaving is not an action on what is
   * on screen, and somebody looking for the way out looks up and left.
   *
   * It lives here rather than in each caller because the column belongs to
   * this component. The profile had to rebuild the width and the padding by
   * hand to get its back button over the title, and two elements only align
   * reliably when one box lays both of them out.
   */
  export let onBack: (() => void) | null = null;

  /**
   * Content laid out on the canvas rather than on the white panel.
   *
   * For a screen made of cards of its own — the company profile — where the
   * panel is a box around boxes. A step of the guide keeps it: its content is
   * one form and the panel is what holds it together.
   */
  export let bare: boolean = false;
  export let backLabel: string = '';
</script>

<div class="flex h-full flex-col">
  <div class="min-h-0 flex-1 overflow-y-auto" style="scrollbar-gutter: stable;">
    <!--
      One column for the title and the card, centred horizontally and starting
      at the top.

      It used to centre vertically too — `my-auto`, chosen over
      `justify-center` because centring a flex child that can outgrow its
      container clips the top of it, and these screens do outgrow it. The
      reasoning was about *how* to centre safely; whether to centre at all went
      unexamined, and it was wrong twice over.

      Against the rest of the app: the dashboard starts at the top, so moving
      between it and a guide step made the whole page slide up or down for no
      reason a customer could attribute to anything they did.

      Against itself, which is worse: no two steps in the guide are the same
      height. Centred, every step put its title somewhere different, so the one
      element that should be an anchor through a sequence was the element that
      moved most. Pinned to the top, the title stays where it was and only the
      content under it changes.

      `py-10` matches the dashboard's, so the two genuinely begin at the same
      height rather than approximately.
    -->
    <div
      class="mx-auto flex min-h-full w-full flex-col px-4 py-10 sm:px-6 lg:px-8 {wide
        ? 'max-w-[88rem]'
        : 'max-w-5xl'}"
    >
      <div>
        {#if onBack}
          <button
            type="button"
            on:click={onBack}
            class="-ml-2 mb-3 inline-flex items-center gap-1.5 rounded-control px-2 py-1.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-900"
          >
            <ArrowLeft class="h-4 w-4" />
            {backLabel}
          </button>
        {/if}

        <ScreenHeader {icon} {title} {subtitle} {step} {steps}>
          <slot name="actions" slot="actions" />
        </ScreenHeader>

        <!--
          Room between the title and the thing it titles.

          Ten rather than eight. At `mt-8` the heading sat close enough to the
          card to read as attached to it — a label on the box rather than the
          name of the screen — which is the opposite of what taking it out of
          its own white band was for.
        -->
        <div class="mt-10">
          <!--
        One card, one size.

        Every screen puts its content on the same white panel, and the panel
        has a floor: without `min-h` a short step drew a small box and a long
        one drew a tall box, so moving through the guide made the page jump —
        the footer walking up and down the window as the content changed.

        A floor rather than a fixed height, because the industry tree is
        genuinely taller than a founding-year slider and cropping it to match
        would be worse than the jump.

        The title stays outside the card, on the canvas: a heading above a panel
        rather than a line inside it.
      -->
          <div
            class={bare
              ? 'flex flex-col'
              : 'flex min-h-[22rem] flex-col rounded-surface border border-hairline bg-surface-panel p-6'}
          >
            <div class="flex-1"><slot /></div>

            {#if $$slots.footer}
              <!--
            The buttons belong to the card, not to the window.

            They were a band pinned to the foot of the screen, which put them
            beside the summary bar and made two rows of controls at the bottom
            of the page — and left them a long way from the thing they act on
            when a step was short. Inside the card they sit under their own
            content, at its foot: `flex-1` on the body pushes them down, so
            they land in the same place whether the step is two controls or
            twenty.

            Full-bleed rule above them: the negative margin lets the line reach
            the card's edges while the buttons keep the card's padding.
          -->
              <div
                class="-mx-6 mt-6 flex items-center justify-between gap-3 border-t border-hairline px-6 pt-4"
              >
                <slot name="footer" />
              </div>
            {/if}
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
